/**
 * CLI telemetry v2 — origin / machine / args / intent collection.
 *
 * Pure, testable helpers that read the process environment, argv, and TTY
 * state to enrich a cli-telemetry event with the v2 contract's optional
 * context fields (design 2026-07-13-cli-telemetry-v2-pubsub-bigquery-design).
 * Every function takes its inputs as parameters (defaulting to the live
 * process) so tests can drive them deterministically.
 *
 * Fresh build — does NOT reuse the reverted conversation-log subsystem
 * (#1813). Everything collected here is best-effort and privacy-bounded:
 *
 *  - `caller_kind` / `client_name` are inferred from a bounded set of env
 *    markers + TTY state, never from arbitrary env values.
 *  - `invocation_signals` records PRESENCE (or a low-cardinality value) of a
 *    fixed whitelist of env markers — never a raw secret env value.
 *  - `args` keeps flag NAMES and credential-scrubs flag VALUES; positional
 *    operands (entity names, paths) are dropped, not recorded.
 *  - `cwd_hint` is the working-directory BASENAME only — never the full path.
 *
 * The consent gate lives in `recordCliEvent` (telemetry.ts): this module is
 * only ever invoked once an event has cleared that gate.
 */

import * as os from 'node:os';
import { basename } from 'node:path';
import { scrubLocal } from './telemetry.js';
import type { CallerKind } from '../../../shared/src/telemetry/cli-telemetry-v2.js';

// ── Bounds (mirror the server-side caps in cli-telemetry-v2-schema.ts) ──────

const MAX_ARGS_KEYS = 32;
const MAX_ARG_KEY_LEN = 64;
const MAX_ARG_VALUE_LEN = 512;
const MAX_SIGNAL_KEYS = 32;
const MAX_SIGNAL_VALUE_LEN = 256;
const MAX_CWD_HINT_LEN = 128;
const MAX_CLIENT_NAME_LEN = 128;
const MAX_ARCH_LEN = 32;
const MAX_OS_VERSION_LEN = 64;
const MAX_FRAMEWORK_LEN = 64;
const MAX_ENTITY_TYPE_LEN = 32;
const MAX_DESCRIPTION_LEN = 2_000;

type Env = Record<string, string | undefined>;

/** Truthy-ish check for a presence env marker (any non-empty value counts). */
function present(v: string | undefined): boolean {
  return typeof v === 'string' && v.trim().length > 0;
}

// ── caller_kind / client_name ──────────────────────────────────────────────

/** Env vars that mark a known CI system (presence ⇒ `ci`). */
const CI_MARKERS: ReadonlyArray<string> = [
  'CI',
  'GITHUB_ACTIONS',
  'GITLAB_CI',
  'CIRCLECI',
  'BUILDKITE',
  'JENKINS_URL',
  'TF_BUILD',
  'TEAMCITY_VERSION',
  'TRAVIS',
];

/** True when any known CI marker is present. */
function looksLikeCi(env: Env): boolean {
  return CI_MARKERS.some((k) => present(env[k]));
}

/** True when an agent marker is present (Claude Code / an explicit client). */
function looksLikeAgent(env: Env): boolean {
  return present(env.CLAUDECODE) || present(env.CLOUDGRID_CLIENT);
}

/**
 * Best-effort source detection. Precedence, most-specific first:
 *
 *  1. `CLOUDGRID_CALLER_KIND` — explicit override (an MCP server / agent that
 *     knows what it is can declare itself). Wins when it names a valid kind.
 *  2. `mcp-stdio` — an MCP-launch marker present AND no TTY.
 *  3. `agent` — `CLAUDECODE` / `CLOUDGRID_CLIENT` present.
 *  4. `ci` — a known CI env marker present.
 *  5. `cli` — an interactive TTY, none of the above.
 *  6. `unknown` — no signal at all (no TTY, no markers, not CI).
 */
export function detectCallerKind(
  env: Env = process.env,
  isTty: boolean = Boolean(process.stdout.isTTY),
): CallerKind {
  const override = env.CLOUDGRID_CALLER_KIND?.trim();
  if (
    override === 'cli' ||
    override === 'mcp-stdio' ||
    override === 'agent' ||
    override === 'ci' ||
    override === 'unknown'
  ) {
    return override;
  }
  if ((present(env.CLOUDGRID_MCP) || present(env.MCP_SERVER)) && !isTty) return 'mcp-stdio';
  if (looksLikeAgent(env)) return 'agent';
  if (looksLikeCi(env)) return 'ci';
  if (isTty) return 'cli';
  return 'unknown';
}

/** Human-readable CI system name for a present marker, else null. */
function ciSystemName(env: Env): string | null {
  if (present(env.GITHUB_ACTIONS)) return 'GitHub Actions';
  if (present(env.GITLAB_CI)) return 'GitLab CI';
  if (present(env.CIRCLECI)) return 'CircleCI';
  if (present(env.BUILDKITE)) return 'Buildkite';
  if (present(env.JENKINS_URL)) return 'Jenkins';
  if (present(env.TF_BUILD)) return 'Azure Pipelines';
  if (present(env.TRAVIS)) return 'Travis CI';
  if (present(env.CI)) return 'CI';
  return null;
}

/**
 * Best-effort client name. Explicit `CLOUDGRID_CLIENT` wins; then Claude Code;
 * then the CI system; then the terminal program (`TERM_PROGRAM`). Bounded, no
 * secret-shaped values (it is a low-cardinality label).
 */
export function detectClientName(
  env: Env = process.env,
  kind: CallerKind = detectCallerKind(env),
): string | undefined {
  const explicit = env.CLOUDGRID_CLIENT?.trim();
  if (present(explicit)) return explicit!.slice(0, MAX_CLIENT_NAME_LEN);
  if (present(env.CLAUDECODE)) return 'Claude Code';
  if (kind === 'ci') {
    const ci = ciSystemName(env);
    if (ci) return ci.slice(0, MAX_CLIENT_NAME_LEN);
  }
  if (kind === 'cli' && present(env.TERM_PROGRAM)) {
    return env.TERM_PROGRAM!.trim().slice(0, MAX_CLIENT_NAME_LEN);
  }
  return undefined;
}

/** Headless ⇒ no interactive stdout TTY. */
export function detectHeadless(isTty: boolean = Boolean(process.stdout.isTTY)): boolean {
  return !isTty;
}

// ── invocation_signals ─────────────────────────────────────────────────────

/**
 * The bounded whitelist of env markers we record in `invocation_signals`. For
 * `includeValue: false` keys we record only PRESENCE (`"true"`); for
 * `includeValue: true` keys the value is low-cardinality and safe to keep
 * (still credential-scrubbed + length-capped defensively). NEVER add a key
 * whose value can hold a secret with `includeValue: true`.
 */
const SIGNAL_WHITELIST: ReadonlyArray<{ key: string; includeValue: boolean }> = [
  { key: 'CLAUDECODE', includeValue: false },
  { key: 'CI', includeValue: false },
  { key: 'GITHUB_ACTIONS', includeValue: false },
  { key: 'GITLAB_CI', includeValue: false },
  { key: 'CIRCLECI', includeValue: false },
  { key: 'BUILDKITE', includeValue: false },
  { key: 'JENKINS_URL', includeValue: false },
  { key: 'TF_BUILD', includeValue: false },
  { key: 'SSH_TTY', includeValue: false },
  { key: 'TMUX', includeValue: false },
  { key: 'CLOUDGRID_SESSION_ID', includeValue: false },
  { key: 'CLOUDGRID_TRACE_ID', includeValue: false },
  { key: 'CLOUDGRID_MCP', includeValue: false },
  { key: 'TERM', includeValue: true },
  { key: 'TERM_PROGRAM', includeValue: true },
  { key: 'CLOUDGRID_CLIENT', includeValue: true },
  { key: 'CLOUDGRID_CALLER_KIND', includeValue: true },
];

/**
 * Collect the bounded env-marker signals. Presence keys map to `"true"`;
 * value keys carry their scrubbed, capped value. Absent markers are omitted.
 * Naturally bounded by the whitelist length (well under {@link MAX_SIGNAL_KEYS}).
 */
export function collectInvocationSignals(env: Env = process.env): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { key, includeValue } of SIGNAL_WHITELIST) {
    if (Object.keys(out).length >= MAX_SIGNAL_KEYS) break;
    const raw = env[key];
    if (!present(raw)) continue;
    out[key] = includeValue ? scrubLocal(raw!).slice(0, MAX_SIGNAL_VALUE_LEN) : 'true';
  }
  return out;
}

// ── machine / env ──────────────────────────────────────────────────────────

/**
 * Non-identifying machine facts beyond the always-present `os` / versions.
 * `arch` is the CPU arch; `os_version` is the kernel/OS release; `cwd_hint`
 * is the working-directory BASENAME only — never the full path.
 */
export function collectMachine(cwd: string = process.cwd()): {
  arch: string;
  os_version: string;
  cwd_hint: string;
} {
  return {
    arch: String(process.arch).slice(0, MAX_ARCH_LEN),
    os_version: os.release().slice(0, MAX_OS_VERSION_LEN),
    cwd_hint: scrubLocal(basename(cwd)).slice(0, MAX_CWD_HINT_LEN),
  };
}

// ── args ────────────────────────────────────────────────────────────────────

/**
 * Flag NAMES that carry a secret VALUE regardless of the value's shape. The
 * pattern-based {@link scrubLocal} only catches value-shaped credentials
 * (JWT / `sk_live_` / `Bearer` …); an OPAQUE secret behind one of these flags
 * (`--password hunter2`, `--claim-token abc123`) would otherwise pass through.
 * When the flag name matches, the whole value is redacted (the NAME is kept).
 * The server enforces the same rule (`scrubArgsValues` in
 * `cli-telemetry-v2-schema.ts`) — keep the two patterns in sync.
 */
export const SENSITIVE_FLAG_RE =
  /(^|[-_])(token|secret|password|passwd|key|auth|credential|api[_-]?key)([-_]|$)/i;

/** Redact a flag value by NAME (opaque secret) then by VALUE shape (scrubLocal). */
function scrubArgValue(name: string, value: string): string {
  if (SENSITIVE_FLAG_RE.test(name)) return '[REDACTED]';
  return scrubLocal(value);
}

/**
 * Parse the invoked command's flags/options into a bounded `Record<string,string>`.
 *
 * Rules:
 *  - Only `--flag` / `-f` tokens become keys (leading dashes stripped); positional
 *    operands (verb, entity name, paths) are DROPPED — they can carry PII.
 *  - `--flag=value` and `--flag value` both yield `{ flag: value }`.
 *  - A flag with no following value (next token is another flag, or end of argv)
 *    is a boolean ⇒ `"true"`.
 *  - VALUES are scrubbed by flag NAME ({@link SENSITIVE_FLAG_RE}) and by value
 *    shape ({@link scrubLocal}) — defense-in-depth; the server scrubs again.
 *  - Bounded: ≤ {@link MAX_ARGS_KEYS} keys, key/value length-capped.
 *
 * `argv` is the raw `process.argv` (the leading `node` + script entries are
 * sliced off here).
 */
export function parseArgs(argv: ReadonlyArray<string> = process.argv): Record<string, string> {
  const out: Record<string, string> = {};
  const args = argv.slice(2);
  for (let i = 0; i < args.length; i++) {
    const tok = args[i]!;
    if (!tok.startsWith('-') || tok === '-' || tok === '--') continue;
    let key: string;
    let value: string | undefined;
    const eq = tok.indexOf('=');
    if (eq !== -1) {
      key = tok.slice(0, eq);
      value = tok.slice(eq + 1);
    } else {
      key = tok;
      const next = args[i + 1];
      if (next !== undefined && !next.startsWith('-')) {
        value = next;
        i++; // consume the value token
      }
    }
    const name = key.replace(/^-+/, '').trim();
    if (!name) continue;
    if (!(name in out) && Object.keys(out).length >= MAX_ARGS_KEYS) continue;
    const rawVal = value === undefined ? 'true' : value;
    out[name.slice(0, MAX_ARG_KEY_LEN)] = scrubArgValue(name, rawVal).slice(0, MAX_ARG_VALUE_LEN);
  }
  return out;
}

// ── intent ──────────────────────────────────────────────────────────────────

/** Known entity kinds that can appear as the `grid init <kind>` positional. */
const ENTITY_KINDS: ReadonlyArray<string> = ['app', 'agent', 'inspiration', 'drop', 'service'];

/**
 * Infer what the user was trying to create, when it can be read cheaply off the
 * argv/args. Best-effort and sparse — every field is optional:
 *
 *  - `entity_type` — the `grid init <kind>` positional (`app` | `agent` | …).
 *  - `framework`   — `--type` / `--framework` value (node | next | python …).
 *  - `description` — `--description` value (already scrubbed via {@link parseArgs}).
 */
export function inferIntent(
  argv: ReadonlyArray<string> = process.argv,
  args: Record<string, string> = parseArgs(argv),
): { entity_type?: string; framework?: string; description?: string } {
  const out: { entity_type?: string; framework?: string; description?: string } = {};

  const positionals = argv.slice(2).filter((a) => !a.startsWith('-'));
  const initIdx = positionals.indexOf('init');
  if (initIdx !== -1) {
    const kind = positionals[initIdx + 1];
    if (kind && ENTITY_KINDS.includes(kind)) out.entity_type = kind.slice(0, MAX_ENTITY_TYPE_LEN);
  }

  const framework = args.type ?? args.framework;
  if (present(framework)) out.framework = framework!.slice(0, MAX_FRAMEWORK_LEN);

  if (present(args.description)) out.description = args.description!.slice(0, MAX_DESCRIPTION_LEN);

  return out;
}
